import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Search, Loader2 } from "lucide-react";
import { searchInstruments, addInstrument, addTransaction } from "../lib/api";

const KIND = { mutual_fund: "Mutual Fund", stock: "Stock", etf: "ETF", crypto: "Crypto", commodity: "Commodity ETF", debt: "Debt ETF",
  large: "Large-cap", mid: "Mid-cap", small: "Small-cap" };
const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const inputCls = "w-full mt-1 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-3 py-2 font-mono text-sm text-[#f8fafc] focus:border-[#00e5ff]/60 outline-none";

function InstrumentSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    setBusy(true);
    const t = setTimeout(() => searchInstruments(q).then((d) => setRes(d.results)).finally(() => setBusy(false)), 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 mt-0.5 text-[#64748b]" />
        <input autoFocus data-testid="holding-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search stock, ETF, mutual fund, crypto…" className={`${inputCls} pl-9 font-sans`} />
        {busy && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 animate-spin text-[#00e5ff]" />}
      </div>
      <div className="mt-2 max-h-64 overflow-y-auto space-y-1" data-testid="holding-search-results">
        {res.map((r) => (
          <button key={r.key} data-testid={`search-result-${r.symbol.replace(/[^A-Za-z0-9]/g, "-").toLowerCase()}`} onClick={() => onPick(r)}
            className="w-full text-left px-3 py-2 rounded-lg border border-[#1e293b] hover:border-[#00e5ff]/50 transition-colors">
            <div className="text-sm text-[#f8fafc] truncate">{r.name}</div>
            <div className="font-mono text-[10px] text-[#64748b]">{r.symbol} · {KIND[r.kind] || r.kind} · {r.exchange} · {r.currency}</div>
          </button>
        ))}
        {q.trim().length >= 2 && !busy && res.length === 0 && <div className="text-xs text-[#64748b] px-1 py-2" data-testid="holding-search-empty">No matches. Try the ticker (e.g. INFY, AAPL) or fund name.</div>}
      </div>
    </div>
  );
}

// Buy / sell transaction modal with market-wide instrument search.
export default function AddHoldingModal({ prefill = {}, goals = [], lockInstrument = false, title, onClose, onSaved }) {
  const [inst, setInst] = useState(prefill.instrument_id ? { id: prefill.instrument_id, name: prefill.name, currency: prefill.currency } : null);
  const [loading, setLoading] = useState(false);
  const [side, setSide] = useState(prefill.side || "buy");
  const [qty, setQty] = useState(prefill.qty || "");
  const [price, setPrice] = useState(prefill.price || prefill.avg_cost || "");
  const [date, setDate] = useState(today());
  const [fees, setFees] = useState("");
  const [goalId, setGoalId] = useState(prefill.goal_id || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = async (r) => {
    setLoading(true); setErr("");
    try {
      const d = await addInstrument(r);
      const px = Number(d.price.toFixed(d.price < 1 ? 6 : 2));
      setInst({ ...d, price: px });
      setPrice(px);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load instrument"); } finally { setLoading(false); }
  };
  const save = async () => {
    if (!inst || !qty || !price || !date) { setErr("Quantity, price and date are required"); return; }
    setBusy(true); setErr("");
    try {
      await addTransaction({ instrument_id: inst.id, side, qty: Number(qty), price: Number(price), date, fees: Number(fees || 0), goal_id: goalId || null });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const cur = inst?.currency === "USD" ? "$" : "₹";
  const total = Number(qty || 0) * Number(price || 0) + (side === "buy" ? 1 : -1) * Number(fees || 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(4,7,13,0.75)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()} className="ao-card w-full max-w-md p-6 max-h-[92dvh] overflow-y-auto" data-testid="holding-modal">
        <div className="flex items-center justify-between mb-4"><div className="text-lg font-semibold">{title || "Add Transaction"}</div>
          <button data-testid="holding-modal-close" onClick={onClose} className="text-[#64748b] hover:text-[#f8fafc]"><X className="w-5 h-5" /></button></div>

        {!inst ? (loading ? <div className="py-10 text-center text-sm text-[#94a3b8]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading live data…</div> : <InstrumentSearch onPick={pick} />) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-[#2d3c59] px-3 py-2 mb-4" data-testid="holding-selected">
              <div className="min-w-0"><div className="text-sm text-[#f8fafc] truncate">{inst.name}</div>
                <div className="font-mono text-[10px] text-[#64748b]">{inst.symbol}{inst.price ? ` · live ${cur}${inst.price}` : ""}</div></div>
              {!lockInstrument && <button data-testid="holding-change" onClick={() => setInst(null)} className="text-xs text-[#00e5ff] shrink-0 ml-2">Change</button>}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {["buy", "sell"].map((s) => (
                <button key={s} data-testid={`txn-side-${s}`} onClick={() => setSide(s)}
                  className={`py-2 rounded-lg text-sm font-medium uppercase transition-colors ${side === s ? (s === "buy" ? "bg-[#00e676] text-[#06121a]" : "bg-[#ff3b30] text-white") : "border border-[#2d3c59] text-[#94a3b8]"}`}>{s}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline">Quantity / units</label><input data-testid="holding-qty" type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} /></div>
              <div><label className="overline">{side === "buy" ? "Purchase" : "Sell"} price ({cur})</label><input data-testid="holding-price" type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} /></div>
              <div><label className="overline">{side === "buy" ? "Purchase" : "Sell"} date</label><input data-testid="holding-date" type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
              <div><label className="overline">Charges ({cur}, optional)</label><input data-testid="holding-fees" type="number" min="0" step="any" value={fees} onChange={(e) => setFees(e.target.value)} className={inputCls} /></div>
            </div>
            {side === "buy" && (
              <>
                <label className="overline block mt-3">Link to Goal (optional)</label>
                <select data-testid="holding-goal" value={goalId} onChange={(e) => setGoalId(e.target.value)} className={`${inputCls} font-sans`}>
                  <option value="">— No goal (general) —</option>
                  {goals.map((g) => <option key={g.id} value={g.id}>{g.name}{g.year ? ` (${g.year})` : ""}</option>)}
                </select>
              </>
            )}
            <div className="mt-3 text-xs text-[#94a3b8] font-mono" data-testid="txn-total">Total {side === "buy" ? "invested" : "proceeds"}: {cur}{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </>
        )}
        {err && <div className="mt-3 text-xs text-[#ff3b30]" data-testid="holding-error">{err}</div>}
        {inst && (
          <button data-testid="save-holding-btn" disabled={busy} onClick={save}
            className="mt-5 w-full rounded-lg bg-[#00e5ff] text-[#06121a] font-medium py-2.5 hover:bg-[#33eaff] disabled:opacity-50">{busy ? "Saving…" : `Save ${side === "buy" ? "Purchase" : "Sale"}`}</button>
        )}
      </motion.div>
    </motion.div>
  );
}
